import { GearItem, GearStat, RARITY_IDS, gearStatUse, primaryOn, primaryStatOn } from "@highmarch/domain";
import { itemScore } from "@highmarch/app";
import type { OwnerView, SocketView } from "@highmarch/app";
import { HubScene } from "../../render/hubScene";
import { RARITY_LABEL, SLOT_GLYPH, pct, useNote } from "../gearUi";
import { icon } from "../iconsKit";
import { gearIconUrl, portraitUrl } from "../portraits";
import type { ArmouryCtx } from "./host";

/** Short stat words on sockets and stat chips (a tower's damage is the fortress's strike damage). */
const STAT_SHORT: Record<GearStat, string> = { [GearStat.Damage]: "dmg", [GearStat.Range]: "range", [GearStat.FireRate]: "rate", [GearStat.Hp]: "HP", [GearStat.Points]: "points" };
const STAT_WORD: Record<GearStat, string> = { [GearStat.Damage]: "Damage", [GearStat.Range]: "Range", [GearStat.FireRate]: "Fire rate", [GearStat.Hp]: "Fortress HP", [GearStat.Points]: "Points" };
/** Socket corners in slot order: Mount/Wall top-left, Sight/Banner top-right, Ammo/Forge bottom-left, Charm/Relic bottom-right. */
const CORNER = ["l t", "r t", "l b", "r b"];

export const statShort = (s: GearStat): string => STAT_SHORT[s];
export const ownerName = (v: OwnerView): string => (v.isBase ? "Fortress" : v.name);
export const ownerArt = (v: OwnerView): string | null => portraitUrl(v.isBase ? "base" : v.id, HubScene.tierFor(v.level));

/** The affinity as one small chip under the name ("★ Range"): what the tower loves, no sentence (the ★ on its items). */
function affinityHtml(v: OwnerView): string {
  if (v.isBase || v.affinity < 0) return "";
  const word = STAT_WORD[v.affinity as GearStat];
  const what = AFFINITY_NOTE[v.id];
  return `<span class="aff" aria-label="Loves ${word.toLowerCase()} items${what ? `: ${what}` : ""}">★ ${word}</span>`;
}

/**
 * What a TOWERS_V2 tower's affinity stat does for it (gear.json `affinity`), since none of them simply shoots: the
 * Mortar's shells, the Barracks' soldiers' blows, the Arcane Spire's pulses, the Hex Totem's aura.
 */
export const AFFINITY_NOTE: Readonly<Record<string, string>> = { mortar: "every shell", barracks: "its soldiers' blows", arcane: "faster pulses", hex: "a wider curse" };

function setHtml(ctx: ArmouryCtx, v: OwnerView): string {
  const g = ctx.B.gear;
  const pips = Array.from({ length: v.set.needed }, (_, i) => `<i class="${i < v.set.pieces ? "on" : ""}"></i>`).join("");
  const label = `${RARITY_LABEL[g.setMinRarity]} set, ${Math.min(v.set.pieces, v.set.needed)} of ${v.set.needed}: ${pct(g.setDamage)} damage and range${v.set.active ? ", on" : ""}`;
  return `<div class="setbar${v.set.active ? " on" : ""}" aria-label="${label}"><span class="pips">${pips}</span><span class="t">Set</span><b>${pct(g.setDamage)} DMG·RNG</b></div>`;
}

function discHtml(ctx: ArmouryCtx, it: GearItem): string {
  const slot = ctx.B.gear.slots[it.slot];
  return `<img class="gi" decoding="async" src="${gearIconUrl(slot.id, RARITY_IDS[it.rarity])}" alt=""><i class="plus">Lv ${it.level}</i>`;
}

/** The "n new" tag on a socket's disc: neutral (information, like the Stash tab's "n new"), not an action-red count. */
const newTagHtml = (n: number): string => (n > 0 ? `<i class="nnew">${n} new</i>` : "");

function socketHtml(ctx: ArmouryCtx, v: OwnerView, s: SocketView, k: number, fresh: number): string {
  const B = ctx.B;
  const slot = B.gear.slots[s.slot];
  const worn = s.worn;
  // Kingshot's hero gear: a rarity-framed square; the item's power under it; a green arrow when the stash holds a
  // better one, a red dot on an empty socket something fits. NEW items that beat what it holds get their tag.
  const nnew = newTagHtml(fresh);
  let disc: string, under: string, cls: string;
  if (worn) {
    cls = ` r${worn.rarity}${s.better > 0 ? " better" : ""}`;
    disc = `<span class="disc r${worn.rarity}">${discHtml(ctx, worn)}${s.better > 0 ? `<i class="upg">${icon("chev", 14)}</i>` : ""}${nnew}</span>`;
    under = `<span class="pw">${icon("bolt", 11)}${itemScore(B, worn)}</span>`;
  } else {
    cls = ` none${s.better > 0 ? " fit" : ""}`;
    disc = `<span class="disc empty${s.better > 0 ? " fit" : ""}">${icon(SLOT_GLYPH[slot.id] ?? "bag", 26)}${s.better > 0 ? `<i class="rdot"></i>` : ""}${nnew}</span>`;
    under = `<span class="lbl">${slot.name}</span>`;
  }
  const stat = worn ? primaryStatOn(B, worn, v.owner) : null;
  const value = worn && stat !== null ? `, ${pct(primaryOn(B, worn, v.owner))} ${STAT_SHORT[stat]}${stat === v.affinity ? " (loved)" : ""}${useNote(gearStatUse(B, v.owner, stat), ownerName(v))}` : "";
  const label = `${slot.name}: ${worn ? `${RARITY_LABEL[worn.rarity]} Lv ${worn.level}${value}` : "empty"}${s.better > 0 ? `, ${s.better} better in the stash` : ""}${fresh > 0 ? `, ${fresh} new` : ""}`;
  return `<button type="button" class="sock ${CORNER[k] ?? "l t"}${cls}" data-slot="${s.slot}" aria-label="${label}">${disc}${under}</button>`;
}

/**
 * The paper doll for one owner (Kingshot's hero gear screen): name, level and gear power, the affinity chip, the big
 * portrait between two columns of rarity-framed sockets (worn with its power / a green arrow when the stash has better
 * / empty with a red dot when something fits; "n new" when items new this visit beat what a socket holds — `fresh`,
 * by slot, from `newPerSocket`), the active Mythic effects and the one-line Epic set tracker. A locked type shows its
 * silhouette and unlock level.
 */
export function stageHtml(ctx: ArmouryCtx, v: OwnerView, fresh: ReadonlyMap<number, number> = new Map()): string {
  const art = ownerArt(v);
  const img = art ? `<img class="portrait" decoding="async" src="${art}" alt="">` : "";
  if (!v.owned) {
    return `<div class="arm-stage lockd"><div class="who"><span class="n disp">${ownerName(v)}</span></div>${img}<div class="lockmsg">${icon("lock", 14)} Level ${v.unlockLevel}</div></div>`;
  }
  const mythic = v.bonus.mythic.map((m) => `<span class="chip myth">${icon("star", 11)} ${ctx.B.gear.mythicEffects[m].name}</span>`).join("");
  return `<div class="arm-stage${v.isBase ? " fort" : ""}">
    <div class="who"><span class="n disp">${ownerName(v)} <span class="lv">Lv ${v.level}</span></span><span class="sub"><span class="pwr disp" aria-label="Gear power ${v.power}">${icon("bolt", 13)}${v.power}</span>${affinityHtml(v)}</span></div>
    ${img}
    ${v.sockets.map((s, k) => socketHtml(ctx, v, s, k, fresh.get(s.slot) ?? 0)).join("")}
    ${mythic ? `<div class="sum">${mythic}</div>` : ""}
    ${setHtml(ctx, v)}
  </div>`;
}
